import { createSelector, type MemoizedSelector } from '@ngrx/store';
import {
  type AdvancedFeasibilityFilters,
  type SurveyLocationUploadState,
  type SurveyMarginFormState,
  type SurveyMetadataFormState,
  type SurveyQualificationsFormState,
  type SurveyQuotasFormState,
  SurveyMarginEntryInspector,
} from '@ps/survey-utils';
import { surveyCreationFeature } from '../survey-creation.feature';
import { DEFAULT_CHILD_AUDIENCE_STATE } from '../survey-creation-state.constants';
import { resolveActiveChildKey } from './utils/audience-children.helper';
import { selectSurvey } from '../core/survey-creation-core.selectors';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyCreationChildAudienceState } from '../survey-creation-state.model';
import { SuppliersMetaData } from './survey-creation-suppliers-meta-data-state.model';

const { selectAudience, selectCore } = surveyCreationFeature;

type ChildSelector = MemoizedSelector<object, SurveyCreationChildAudienceState>;

type AudienceFormState = Pick<
  SurveyCreationChildAudienceState,
  'metadata' | 'qualifications' | 'quotas'
>;

/**
 * The child currently being worked on — see `resolveActiveChildKey`. Falls
 * back to a fresh default child so downstream selectors never have to
 * null-check a missing entry.
 */
const selectActiveChild: ChildSelector = createSelector(
  selectAudience,
  selectCore,
  (state, core) => {
    const key = resolveActiveChildKey(state, core.activeChildKey);
    return state.children[key] ?? { ...DEFAULT_CHILD_AUDIENCE_STATE, key };
  },
);

// Any child by key, for work that runs on a child other than the active one.
const selectChildFor = (key: SurveyChildKey): ChildSelector =>
  createSelector(
    selectAudience,
    (state) => state.children[key] ?? { ...DEFAULT_CHILD_AUDIENCE_STATE, key },
  );

// One projection per field, shared by the active child and any child by key.
const metadataFormOf = (
  child: ChildSelector,
): MemoizedSelector<object, SurveyMetadataFormState> =>
  createSelector(child, ({ metadata }) => metadata);

const qualificationsFormOf = (
  child: ChildSelector,
): MemoizedSelector<object, SurveyQualificationsFormState> =>
  createSelector(child, ({ qualifications }) => qualifications);

const quotasFormOf = (
  child: ChildSelector,
): MemoizedSelector<object, SurveyQuotasFormState> =>
  createSelector(child, ({ quotas }) => quotas);

const surveyFormStateOf = (
  child: ChildSelector,
): MemoizedSelector<object, AudienceFormState> =>
  createSelector(
    metadataFormOf(child),
    qualificationsFormOf(child),
    quotasFormOf(child),
    (metadata, qualifications, quotas) => ({
      metadata,
      qualifications,
      quotas,
    }),
  );

const marginFormOf = (
  child: ChildSelector,
): MemoizedSelector<object, SurveyMarginFormState> =>
  createSelector(child, ({ margin }) => margin);

const advancedFeasibilityFiltersOf = (
  child: ChildSelector,
): MemoizedSelector<object, AdvancedFeasibilityFilters> =>
  createSelector(
    child,
    ({ advancedFeasibilityFilters }) => advancedFeasibilityFilters,
  );

const suppliersMetaDataOf = (
  child: ChildSelector,
): MemoizedSelector<object, SuppliersMetaData> =>
  createSelector(child, ({ suppliersMetaData }) => suppliersMetaData);

const locationFileUploadOf = (
  child: ChildSelector,
): MemoizedSelector<object, SurveyLocationUploadState> =>
  createSelector(child, ({ locationFileUpload }) => locationFileUpload);

export const selectMetadataForm = metadataFormOf(selectActiveChild);

export const selectSurveyClickBalancing = createSelector(
  selectMetadataForm,
  ({ value }) => !!value?.clickBalancing,
);

export const selectIsMetadataValid = createSelector(
  selectMetadataForm,
  ({ valid }) => valid,
);

// The live completes (goal) being edited in the audience form — distinct from
// the saved `survey.completesRequired`, which only changes on save.
export const selectMetadataCompletes = createSelector(
  selectMetadataForm,
  ({ value }) => value?.completes ?? null,
);

// Live LOI/IR being edited in the audience form — distinct from the saved
// `survey.expectedLoi`/`survey.expectedIr`, which only change on save.
export const selectMetadataLoi = createSelector(
  selectMetadataForm,
  ({ value }) => value?.loi ?? null,
);

export const selectMetadataIncidence = createSelector(
  selectMetadataForm,
  ({ value }) => value?.incidence ?? null,
);

export const selectMetadataFieldTime = createSelector(
  selectMetadataForm,
  ({ value }) => value?.fieldTime ?? null,
);

export const selectQualificationsForm = qualificationsFormOf(selectActiveChild);

export const selectQuotasForm = quotasFormOf(selectActiveChild);

export const selectSurveyFormState = surveyFormStateOf(selectActiveChild);

/** The applied Feasibility filters (exclusions/inclusions) */
export const selectAdvancedFeasibilityFilters =
  advancedFeasibilityFiltersOf(selectActiveChild);

export const selectLocationFileUpload = locationFileUploadOf(selectActiveChild);

export const selectLinkedTagIds = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.linkedTagIds,
);

export const selectLoadingLinkedTags = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.loadingLinkedTags,
);

export const selectSelectedTagIds = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.selectedTagIds,
);

export const selectLinkTagsError = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.linkTagsError,
);

export const selectSuppliersMetaDataBlendId = createSelector(
  selectActiveChild,
  ({ suppliersMetaData }) => suppliersMetaData.blendId,
);

export const selectSuppliersMetaData = suppliersMetaDataOf(selectActiveChild);

export const selectIsDirty = createSelector(
  selectActiveChild,
  ({ metadata, margin, qualifications, quotas }) =>
    metadata.isDirty ||
    margin.isDirty ||
    qualifications.isDirty ||
    quotas.isDirty,
);

export const selectMarginForm = marginFormOf(selectActiveChild);

export const selectPendingHighCpiConfirmations = createSelector(
  selectActiveChild,
  ({ pendingHighCpiConfirmations }) => pendingHighCpiConfirmations,
);

/**
 * Whether the Target Margin field may be written. Before the draft exists there
 * is no survey to inspect, so the field stays editable.
 */
export const selectCanUpdateTargetMargin = createSelector(
  selectSurvey,
  (survey) =>
    !survey ||
    new SurveyMarginEntryInspector({
      isManualScpiOverrideEnabled: survey.isManualScpiOverrideEnabled,
      isMarginMaxEnabled: survey.isMarginMaxEnabled,
      status: survey.status,
    }).isEntryAllowed(),
);

export const selectIsPreDraft = createSelector(
  selectCore,
  selectAudience,
  (core, state) => !core.rootSurveyId && Object.keys(state.children).length > 1,
);

export const selectAudienceChildren = createSelector(
  selectAudience,
  ({ children }) => children,
);

export const selectHasChildSurveys = createSelector(
  selectCore,
  selectAudience,
  (core, { children }) =>
    !!core.rootSurveyId && Object.keys(children).length > 1,
);

export const selectIsDrawablePreDraft = createSelector(
  selectIsMetadataValid,
  selectIsPreDraft,
  (isMetadataValid, isPreDraft) => isMetadataValid && isPreDraft,
);

// Per child — the same projections over a child by key.
export const selectSurveyFormStateFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, AudienceFormState> =>
  surveyFormStateOf(selectChildFor(key));

export const selectMarginFormFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, SurveyMarginFormState> =>
  marginFormOf(selectChildFor(key));

export const selectAdvancedFeasibilityFiltersFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, AdvancedFeasibilityFilters> =>
  advancedFeasibilityFiltersOf(selectChildFor(key));

export const selectSuppliersMetaDataFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, SuppliersMetaData> =>
  suppliersMetaDataOf(selectChildFor(key));

export const selectLocationFileUploadFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, SurveyLocationUploadState> =>
  locationFileUploadOf(selectChildFor(key));
